import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Chip, TableCell, TableRow, Typography } from "@mui/material";
import { DoNotDisturb } from "@mui/icons-material";

import TruncateText from "../../../components/TruncateText";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import DateRangeFilter from "../../../components/Filters/DateRangeFilter";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Tabs from "../../../components/Tabs";
import Tooltip from "../../../components/Tooltip";
import messages from "../../../assets/locale/messages";
import emptyBoxImg from "../../../assets/images/placeholders/emptyBox.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import {
  areAllParamsEmpty,
  formatDate
} from "../../../utils/Helpers";
import {
  CHIPS_CLASSES,
  REQUEST_STATUS_IDS,
  REQUEST_STATUS
} from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { reviewRequestsListRequest } from "../../../store/Products/actions";
import {
  categoriesLookupRequest,
  subCategoriesLookupRequest,
  countriesLookupRequest,
  voucherCompaniesLookupRequest
} from "../../../store/Lookups/actions";
import { showHideLoader } from "../../../store/Loader/actions";
import "./ReviewRequests.scss";

const ReviewRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { products, shared } = messages[lang];

  const requestsList = useSelector(
    (state) => state.products.reviewRequests.list
  );
  const requestsMeta = useSelector(
    (state) => state.products.reviewRequests.meta
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );
  const subCategoriesLookup = useSelector(
    (state) => state.lookups.subCategories.list
  );
  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );
  const companiesLookup = useSelector(
    (state) => state.lookups.voucherCompanies.list
  );

  const statusFilterOptions = [
    {
      Id: 0,
      Name: products.list.status.edited,
      isEdit: true
    },
    {
      Id: 1,
      Name: products.list.status.newRequest,
      isEdit: false
    }
  ];

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tabStatusValue, setTabStatusValue] = useState([
    REQUEST_STATUS_IDS.find(
      (status) => status.value === REQUEST_STATUS.pending
    ).id
  ]);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilterValue, setCategoryFilterValue] = useState([]);
  const [subCategoryFilterValue, setSubCategoryFilterValue] =
    useState([]);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [companiesFilterValue, setCompaniesFilterValue] = useState(
    []
  );
  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [dateFilterValue, setDateFilterValue] = useState({
    from: null,
    to: null
  });
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    category_ids: [],
    Subcategory_ids: [],
    country_ids: [],
    exporter_ids: [],
    date_range_from: null,
    date_range_to: null,
    isEdit: null
  });

  useEffect(() => {
    dispatch(categoriesLookupRequest());
    dispatch(subCategoriesLookupRequest());
    dispatch(countriesLookupRequest());
    dispatch(voucherCompaniesLookupRequest());
  }, []);

  useEffect(() => {
    dispatch(
      subCategoriesLookupRequest({
        categoryId: categoryFilterValue
      })
    );
  }, [categoryFilterValue]);

  useEffect(() => {
    dispatch(showHideLoader(true));
    let tabStatusValue = null;
    switch (tabValue) {
      case 0:
        tabStatusValue = REQUEST_STATUS_IDS.find(
          (status) => status.value === REQUEST_STATUS.pending
        ).id;
        break;
      case 1:
        tabStatusValue = REQUEST_STATUS_IDS.find(
          (status) => status.value === REQUEST_STATUS.rejected
        ).id;
        break;
      case 2:
        tabStatusValue = REQUEST_STATUS_IDS.find(
          (status) => status.value === REQUEST_STATUS.cancelled
        ).id;
        break;
      default:
        break;
    }
    setClearFilters(true);
    setSearchValue("");
    setPage(1);
    setTabStatusValue([tabStatusValue]);
  }, [tabValue]);

  useEffect(() => {
    if (clearFilters) {
      setCategoryFilterValue([]);
      setSubCategoryFilterValue([]);
      setCountryFilterValue([]);
      setCompaniesFilterValue([]);
      setStatusFilterValue([]);
      setDateFilterValue({
        from: null,
        to: null
      });
      setParamsObject({
        category_ids: [],
        Subcategory_ids: [],
        country_ids: [],
        exporter_ids: [],
        date_range_from: null,
        date_range_to: null
      });
    }
  }, [clearFilters]);

  const requestReviewsList = () => {
    dispatch(
      reviewRequestsListRequest({
        params: {
          page,
          search_value: searchValue,
          status_ids: tabStatusValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    requestReviewsList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestReviewsList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        category_ids: categoryFilterValue,
        Subcategory_ids: subCategoryFilterValue,
        country_ids: countryFilterValue,
        exporter_ids: companiesFilterValue,
        date_range_from: dateFilterValue?.from || null,
        date_range_to: dateFilterValue?.to || null,
        isEdit:
          statusFilterValue.length === 1
            ? !!(
                statusFilterValue.length === 1 &&
                statusFilterValue.includes(0)
              )
            : null
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  useEffect(() => {
    setParamsObject({
      category_ids: [],
      Subcategory_ids: [],
      country_ids: [],
      exporter_ids: [],
      date_range_from: null,
      date_range_to: null,
      isEdit: null
    });
  }, [tabStatusValue]);

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderRequestTableRow = ({
    Id,
    DefaultImage,
    ProductName,
    CategoryName,
    SubCategoryName,
    ExporterName,
    CountryName,
    PublishedDate,
    ProductId,
    IsEdit,
    DefaultRejectionReasonMessage,
    RejectionReasonMessage
  }) => (
    <TableRow key={`request-row-${Id}`}>
      <TableCell>
        <div
          className={`d-flex align-items-center gap-3 ${tabValue === 0 && "pointer "}`}
          onClick={() => {
            tabValue === 0 &&
              navigate(
                ROUTE_PATHS.productDetailsRequest.replace(
                  ":id",
                  ProductId
                )
              );
          }}>
          <img
            src={DefaultImage || noRequestImg}
            onError={onImageError}
            alt="productImage"
            className="product-img border-radius-8"
          />
          {tabValue === 1 &&
          (DefaultRejectionReasonMessage ||
            RejectionReasonMessage) ? (
            <Tooltip
              title={
                <div>
                  <p className="mb-0">
                    <DoNotDisturb color="primary" className="me-1" />
                    <span className="title">{`${ProductName} ${products.list.productRejectionTooltip}`}</span>
                  </p>
                  <ul className="mb-0">
                    {DefaultRejectionReasonMessage && (
                      <li>{DefaultRejectionReasonMessage}</li>
                    )}
                    {RejectionReasonMessage && (
                      <li>{RejectionReasonMessage}</li>
                    )}
                  </ul>
                </div>
              }
              content={`${ProductName?.length > 25 ? `${ProductName?.slice(0, 25 - 1)}...` : ProductName}`}
              lightBlue
              className={"rejection-reason-tooltip"}
            />
          ) : (
            <TruncateText text={ProductName || "__"} />
          )}
        </div>
      </TableCell>
      <TableCell>{CategoryName || "__"}</TableCell>
      <TableCell>{SubCategoryName || "__"}</TableCell>
      <TableCell>{ExporterName || "__"}</TableCell>
      <TableCell>{CountryName || "__"}</TableCell>
      <TableCell>
        {PublishedDate ? formatDate(PublishedDate) : "__"}
      </TableCell>
      {tabValue === 0 && (
        <TableCell>
          <Chip
            label={
              products.list.status[IsEdit ? "edited" : "newRequest"]
            }
            className={`${IsEdit ? CHIPS_CLASSES.yellow : CHIPS_CLASSES.blue}`}
          />
        </TableCell>
      )}
    </TableRow>
  );

  const dateColumnHeaders = [
    products.list.tableHeader.requestDate,
    products.list.tableHeader.rejectDate,
    products.list.tableHeader.cancelDate
  ];

  const renderTabContent = () => (
    <div>
      <div className="table-header my-4">
        <SearchInput
          name="request-search"
          id="review-request-search"
          className="review-request-search"
          value={searchValue}
          inputWrapperClass="search-input-wrapper search-input-wrapper-width small-placeholder-search"
          placeholder={products.list.searchPlaceholder}
          onChange={(value) => {
            setPage(1);
            setSearchValue(value);
          }}
        />
      </div>
      <div className="table-content">
        {requestsList?.length ? (
          <Table
            className={"px-0"}
            headlines={[
              products.list.tableHeader.product,
              products.list.tableHeader.category,
              products.list.tableHeader.subCategory,
              products.list.tableHeader.companyName,
              products.list.tableHeader.companyCountry,
              dateColumnHeaders[tabValue],
              tabValue === 0 ? products.list.tableHeader.status : ""
            ]}
            rows={
              !isLoading &&
              requestsList &&
              requestsList.map((request) =>
                renderRequestTableRow(request)
              )
            }
          />
        ) : (
          <div>
            {!isLoading && (
              <EmptyState
                subTitle={
                  !!searchValue || !areAllParamsEmpty(paramsObject)
                    ? shared.noSearchResults
                    : products.list.noProductsFound
                }
                className="empty-state-container"
                imgSrc={emptyBoxImg}
              />
            )}
          </div>
        )}
      </div>
      <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
        {!isLoading && requestsList?.length > 0 && (
          <Pagination
            count={requestsMeta?.pages}
            page={requestsMeta?.currentPage || page}
            handleChange={(page) => {
              setPage(page);
            }}
            defaultPage={1}
          />
        )}
      </div>
    </div>
  );

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={categoriesLookup}
          name="categories"
          label={products.list.filters.categories}
          selectedOptionsIds={categoryFilterValue}
          setSelectedOptionsIds={setCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          isApplyFilters={isApplyFilters}
          showSearchBar
          searchPlaceholder={
            products.list.filters.searchPlaceholders.categories
          }
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={subCategoriesLookup}
          name="sub categories"
          label={products.list.filters.subCategories}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.subCategories
          }
          selectedOptionsIds={subCategoryFilterValue}
          setSelectedOptionsIds={setSubCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          placeholder={
            !!categoryFilterValue.length
              ? products.list.filters.searchPlaceholders
                  .noSubcategories
              : ""
          }
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={companiesLookup.map((company) => ({
            Id: company.UserId,
            Name: company.CompanyName
          }))}
          name="companies"
          label={products.list.filters.companies}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.companies
          }
          selectedOptionsIds={companiesFilterValue}
          setSelectedOptionsIds={setCompaniesFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={products.list.filters.countries}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.countries
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      {tabValue === 0 && (
        <div className="border-bottom py-3 px-md-5 px-3">
          <ListFilter
            list={statusFilterOptions}
            name="status"
            label={products.list.filters.status}
            selectedOptionsIds={statusFilterValue}
            setSelectedOptionsIds={setStatusFilterValue}
            clearFilters={clearFilters}
            collabsible
            isApplyFilters={isApplyFilters}
            filterModalOpen={filterModalOpen}
          />
        </div>
      )}
      <div className="py-3 px-md-5 px-3">
        <DateRangeFilter
          name="date-range"
          label={products.list.filters.dateRange}
          onRequestFiltering={setDateFilterValue}
          clearFilters={clearFilters}
          collabsible
          selectedDateFrom={dateFilterValue?.from}
          selectedDateTo={dateFilterValue?.to}
          addBottomMargin
        />
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: products.list.tabs.pending,
      content: renderTabContent()
    },
    {
      label: products.list.tabs.rejected,
      content: renderTabContent()
    },
    {
      label: products.list.tabs.cancelled,
      content: renderTabContent()
    }
  ];

  return (
    <div className="products-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-center">
        <div className="title-count-container d-flex justify-content-center align-items-center">
          <Typography variant="h5" className="me-3 title-color">
            {products.requests}
          </Typography>
        </div>
        <div className="title-buttons-container d-flex justify-content-center gap-3 align-items-center">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="products-table-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8 mb-4">
        <div className="tabs-section">
          <Tabs
            tabs={tabsArray}
            value={tabValue}
            handleChange={setTabValue}
            activeTabAdornment={
              isLoading ? null : (
                <span className="records-count faint-blue-bg border-radius-50 p-2">
                  {requestsMeta?.count || 0}
                </span>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewRequests;
